import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Upload,
  File,
  FileText,
  FileImage,
  FileSpreadsheet,
  FileArchive,
  Presentation,
  Trash2,
  Edit3,
  MoreVertical,
  Download,
  Eye,
  EyeOff,
  FolderOpen,
  Plus,
  X,
  Link2,
  GripVertical,
} from 'lucide-react';
import { documentsApi, coursesApi, uploadsApi, CourseDocument, DocumentCategory } from '../../lib/api';
import { SectionLoader } from '../../components/ui/PremiumLoader';
import toast from 'react-hot-toast';

// Document category config with icons and colors
const CATEGORY_CONFIG: Record<DocumentCategory, { label: string; color: string; bgColor: string }> = {
  SYLLABUS: { label: 'Syllabus', color: 'text-purple-400', bgColor: 'bg-purple-500/20' },
  LECTURE_NOTES: { label: 'Lecture Notes', color: 'text-blue-400', bgColor: 'bg-blue-500/20' },
  RESOURCE: { label: 'Resource', color: 'text-emerald-400', bgColor: 'bg-emerald-500/20' },
  ASSIGNMENT: { label: 'Assignment', color: 'text-amber-400', bgColor: 'bg-amber-500/20' },
  SOLUTION: { label: 'Solution', color: 'text-green-400', bgColor: 'bg-green-500/20' },
  TEMPLATE: { label: 'Template', color: 'text-cyan-400', bgColor: 'bg-cyan-500/20' },
  OTHER: { label: 'Other', color: 'text-gray-400', bgColor: 'bg-gray-500/20' },
};

// File type icons
const getFileIcon = (mimeType: string) => {
  if (mimeType.startsWith('image/')) return FileImage;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType === 'text/csv') return FileSpreadsheet;
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return Presentation;
  if (mimeType.includes('pdf') || mimeType.includes('document') || mimeType.includes('word')) return FileText;
  if (mimeType.includes('zip') || mimeType.includes('archive')) return FileArchive;
  return File;
};

// Format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

interface Course {
  id: string;
  title: string;
  slug: string;
  thumbnailUrl?: string;
}

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File | null, data: {
    title: string;
    description: string;
    category: DocumentCategory;
    isPublic: boolean;
    fileUrl?: string;
    fileName?: string;
    fileSize?: number;
    mimeType?: string;
  }, onProgress?: (progress: number) => void) => Promise<void>;
  isUploading: boolean;
}

function UploadModal({ isOpen, onClose, onUpload, isUploading }: UploadModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<DocumentCategory>('RESOURCE');
  const [isPublic, setIsPublic] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [useUrl, setUseUrl] = useState(false);
  const [fileUrl, setFileUrl] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      if (!title) setTitle(droppedFile.name.replace(/\.[^/.]+$/, ''));
    }
  }, [title]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      if (!title) setTitle(selectedFile.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title) {
      toast.error('Please enter a title');
      return;
    }
    if (!useUrl && !file) {
      toast.error('Please select a file');
      return;
    }
    if (useUrl && !fileUrl) {
      toast.error('Please enter a file URL');
      return;
    }

    await onUpload(useUrl ? null : file, {
      title,
      description,
      category,
      isPublic,
      fileUrl: useUrl ? fileUrl : undefined,
      fileName: useUrl ? title : undefined,
      fileSize: useUrl ? 0 : undefined,
      mimeType: useUrl ? 'application/octet-stream' : undefined,
    }, (progress) => setUploadProgress(progress));

    // Reset form
    setFile(null);
    setTitle('');
    setDescription('');
    setCategory('RESOURCE');
    setIsPublic(false);
    setFileUrl('');
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-xl rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(145deg, rgba(30, 30, 35, 0.98) 0%, rgba(20, 20, 25, 0.98) 100%)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-coral/20 flex items-center justify-center">
              <Upload className="w-5 h-5 text-coral" />
            </div>
            <h2 className="text-xl font-semibold text-white">Upload Document</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5 text-white/60" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Toggle between file upload and URL */}
          <div className="flex gap-2 p-1 rounded-xl bg-white/5">
            <button
              type="button"
              onClick={() => setUseUrl(false)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all ${
                !useUrl ? 'bg-coral text-white' : 'text-white/60 hover:text-white'
              }`}
            >
              <Upload className="w-4 h-4" />
              Upload File
            </button>
            <button
              type="button"
              onClick={() => setUseUrl(true)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all ${
                useUrl ? 'bg-coral text-white' : 'text-white/60 hover:text-white'
              }`}
            >
              <Link2 className="w-4 h-4" />
              From URL
            </button>
          </div>

          {/* File Upload Area */}
          {!useUrl ? (
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                isDragging
                  ? 'border-coral bg-coral/10'
                  : file
                  ? 'border-emerald-500/50 bg-emerald-500/10'
                  : 'border-white/20 hover:border-white/30'
              }`}
            >
              {file ? (
                <div className="flex items-center justify-center gap-4">
                  {(() => {
                    const FileIcon = getFileIcon(file.type);
                    return <FileIcon className="w-12 h-12 text-emerald-400" />;
                  })()}
                  <div className="text-left">
                    <p className="text-white font-medium truncate max-w-[200px]">{file.name}</p>
                    <p className="text-white/50 text-sm">{formatFileSize(file.size)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFile(null)}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    <X className="w-5 h-5 text-white/60" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 text-white/40 mx-auto mb-3" />
                  <p className="text-white/70 mb-2">Drag and drop your file here, or</p>
                  <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 text-white cursor-pointer hover:bg-white/15 transition-colors">
                    <FolderOpen className="w-4 h-4" />
                    Browse Files
                    <input
                      type="file"
                      onChange={handleFileChange}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.csv,.zip,.jpg,.jpeg,.png,.gif,.webp"
                    />
                  </label>
                  <p className="text-white/40 text-xs mt-3">
                    PDF, DOC, PPT, XLS, Images, ZIP • Max 50MB
                  </p>
                </>
              )}
            </div>
          ) : (
            <div>
              <label className="label">File URL</label>
              <input
                type="url"
                value={fileUrl}
                onChange={(e) => setFileUrl(e.target.value)}
                placeholder="https://example.com/document.pdf"
                className="input"
              />
              <p className="text-white/40 text-xs mt-2">
                Enter the direct URL to your file (e.g., from Google Drive, Dropbox)
              </p>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="label">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Document title"
              className="input"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="label">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the document..."
              rows={2}
              className="input resize-none"
            />
          </div>

          {/* Category & Public */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as DocumentCategory)}
                className="input"
              >
                {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Visibility</label>
              <button
                type="button"
                onClick={() => setIsPublic(!isPublic)}
                className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl border transition-all ${
                  isPublic
                    ? 'border-emerald-500/50 bg-emerald-500/10'
                    : 'border-white/10 bg-white/5'
                }`}
              >
                <span className="flex items-center gap-2">
                  {isPublic ? (
                    <Eye className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-white/50" />
                  )}
                  <span className={isPublic ? 'text-emerald-400' : 'text-white/70'}>
                    {isPublic ? 'Public Preview' : 'Enrolled Only'}
                  </span>
                </span>
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl text-white/60 hover:text-white hover:bg-white/5 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUploading || (!file && !useUrl) || !title || (useUrl && !fileUrl)}
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              {isUploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {uploadProgress > 0 ? `${uploadProgress}%` : 'Uploading...'}
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload Document
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

export default function CourseDocumentsPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [course, setCourse] = useState<Course | null>(null);
  const [documents, setDocuments] = useState<CourseDocument[]>([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [editingDoc, setEditingDoc] = useState<string | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  // Fetch course and documents
  useEffect(() => {
    const fetchData = async () => {
      if (!courseId) return;

      setIsLoading(true);
      try {
        const [courseRes, docsRes] = await Promise.all([
          coursesApi.getById(courseId),
          documentsApi.getByCourse(courseId),
        ]);

        if (courseRes.data.success) {
          setCourse(courseRes.data.data as Course);
        }

        if (docsRes.data.success) {
          setDocuments(docsRes.data.data?.documents || []);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load course documents');
        navigate('/professor/courses');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [courseId, navigate]);

  // Handle upload
  const handleUpload = async (
    file: File | null,
    data: {
      title: string;
      description: string;
      category: DocumentCategory;
      isPublic: boolean;
      fileUrl?: string;
      fileName?: string;
      fileSize?: number;
      mimeType?: string;
    },
    onProgress?: (progress: number) => void
  ) => {
    if (!courseId) return;

    setIsUploading(true);
    try {
      let response;

      if (file) {
        // File upload with presigned URL flow
        response = await documentsApi.upload(courseId, file, {
          title: data.title,
          description: data.description || undefined,
          category: data.category,
          isPublic: data.isPublic,
        }, onProgress);
      } else if (data.fileUrl) {
        // URL-based document
        const urlResponse = await documentsApi.createFromUrl(courseId, {
          title: data.title,
          description: data.description || undefined,
          fileUrl: data.fileUrl,
          fileName: data.fileName || data.title,
          fileSize: data.fileSize || 0,
          mimeType: data.mimeType || 'application/octet-stream',
          category: data.category,
          isPublic: data.isPublic,
        });
        response = urlResponse.data;
      }

      if (response?.success && response.data) {
        setDocuments((prev) => [response.data as CourseDocument, ...prev]);
        setIsUploadModalOpen(false);
        toast.success('Document uploaded successfully! Students have been notified.');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload document');
    } finally {
      setIsUploading(false);
    }
  };

  // Handle delete
  const handleDelete = async (docId: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      const response = await documentsApi.delete(docId);
      if (response.data.success) {
        setDocuments((prev) => prev.filter((d) => d.id !== docId));
        toast.success('Document deleted');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete document');
    }
    setMenuOpenId(null);
  };

  // Handle update
  const handleUpdate = async (docId: string, updates: Partial<CourseDocument>) => {
    try {
      const response = await documentsApi.update(docId, updates);
      if (response.data.success) {
        setDocuments((prev) =>
          prev.map((d) => (d.id === docId ? { ...d, ...updates } : d))
        );
        setEditingDoc(null);
        toast.success('Document updated');
      }
    } catch (error) {
      console.error('Update failed:', error);
      toast.error('Failed to update document');
    }
  };

  // Group documents by category
  const groupedDocs = documents.reduce((acc, doc) => {
    const cat = doc.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(doc);
    return acc;
  }, {} as Record<DocumentCategory, CourseDocument[]>);

  if (isLoading) {
    return <SectionLoader className="h-full" text="Loading documents..." />;
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <Link
          to="/professor/courses"
          className="flex items-center gap-2 text-white/50 hover:text-white transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Courses</span>
        </Link>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
              <FolderOpen className="w-7 h-7 text-purple-400" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-white">Course Documents</h1>
              <p className="text-white/50">{course?.title}</p>
            </div>
          </div>
          <button
            onClick={() => setIsUploadModalOpen(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            <span className="hidden sm:inline">Upload Document</span>
          </button>
        </div>
      </motion.div>

      {/* Stats Bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8"
      >
        <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10">
          <p className="text-2xl font-bold text-white">{documents.length}</p>
          <p className="text-white/50 text-sm">Total Documents</p>
        </div>
        <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10">
          <p className="text-2xl font-bold text-white">
            {formatFileSize(documents.reduce((sum, d) => sum + d.fileSize, 0))}
          </p>
          <p className="text-white/50 text-sm">Total Size</p>
        </div>
        <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10">
          <p className="text-2xl font-bold text-emerald-400">
            {documents.filter((d) => d.isPublic).length}
          </p>
          <p className="text-white/50 text-sm">Public Previews</p>
        </div>
        <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10">
          <p className="text-2xl font-bold text-white">
            {Object.keys(groupedDocs).length}
          </p>
          <p className="text-white/50 text-sm">Categories</p>
        </div>
      </motion.div>

      {/* Documents List */}
      {documents.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-center py-16"
        >
          <div className="w-20 h-20 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-4">
            <FolderOpen className="w-10 h-10 text-white/30" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">No documents yet</h3>
          <p className="text-white/50 mb-6 max-w-sm mx-auto">
            Upload documents to share resources, lecture notes, and assignments with your students.
          </p>
          <button
            onClick={() => setIsUploadModalOpen(true)}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Upload className="w-5 h-5" />
            Upload Your First Document
          </button>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-6"
        >
          {Object.entries(CATEGORY_CONFIG).map(([category, config]) => {
            const categoryDocs = groupedDocs[category as DocumentCategory] || [];
            if (categoryDocs.length === 0) return null;

            return (
              <div key={category} className="rounded-2xl overflow-hidden border border-white/10 bg-white/[0.02]">
                {/* Category Header */}
                <div className="flex items-center gap-3 px-6 py-4 border-b border-white/10 bg-white/[0.02]">
                  <div className={`w-8 h-8 rounded-lg ${config.bgColor} flex items-center justify-center`}>
                    <FileText className={`w-4 h-4 ${config.color}`} />
                  </div>
                  <h3 className="font-semibold text-white">{config.label}</h3>
                  <span className="text-white/40 text-sm">({categoryDocs.length})</span>
                </div>

                {/* Documents */}
                <div className="divide-y divide-white/5">
                  {categoryDocs.map((doc) => {
                    const FileIcon = getFileIcon(doc.mimeType);
                    const isEditing = editingDoc === doc.id;

                    return (
                      <div
                        key={doc.id}
                        className="flex items-center gap-4 px-6 py-4 hover:bg-white/[0.02] transition-colors group"
                      >
                        {/* Drag Handle */}
                        <GripVertical className="w-4 h-4 text-white/20 opacity-0 group-hover:opacity-100 cursor-grab" />

                        {/* File Icon */}
                        <div className={`w-10 h-10 rounded-xl ${config.bgColor} flex items-center justify-center flex-shrink-0`}>
                          <FileIcon className={`w-5 h-5 ${config.color}`} />
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          {isEditing ? (
                            <input
                              type="text"
                              defaultValue={doc.title}
                              autoFocus
                              onBlur={(e) => handleUpdate(doc.id, { title: e.target.value })}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleUpdate(doc.id, { title: e.currentTarget.value });
                                } else if (e.key === 'Escape') {
                                  setEditingDoc(null);
                                }
                              }}
                              className="input py-1.5 text-sm"
                            />
                          ) : (
                            <>
                              <h4 className="font-medium text-white truncate">{doc.title}</h4>
                              <div className="flex items-center gap-3 text-xs text-white/40">
                                <span>{doc.fileName}</span>
                                <span>•</span>
                                <span>{formatFileSize(doc.fileSize)}</span>
                                <span>•</span>
                                <span>{new Date(doc.createdAt).toLocaleDateString()}</span>
                              </div>
                            </>
                          )}
                        </div>

                        {/* Public Badge */}
                        {doc.isPublic && (
                          <span className="flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-400 text-xs">
                            <Eye className="w-3 h-3" />
                            Public
                          </span>
                        )}

                        {/* Actions */}
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => uploadsApi.downloadFile(doc.fileUrl, doc.fileName)}
                            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                            title="Download"
                          >
                            <Download className="w-4 h-4 text-white/60" />
                          </button>
                          <button
                            onClick={() => setEditingDoc(doc.id)}
                            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                            title="Edit"
                          >
                            <Edit3 className="w-4 h-4 text-white/60" />
                          </button>
                          <div className="relative">
                            <button
                              onClick={() => setMenuOpenId(menuOpenId === doc.id ? null : doc.id)}
                              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                            >
                              <MoreVertical className="w-4 h-4 text-white/60" />
                            </button>
                            
                            <AnimatePresence>
                              {menuOpenId === doc.id && (
                                <motion.div
                                  initial={{ opacity: 0, scale: 0.95 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  exit={{ opacity: 0, scale: 0.95 }}
                                  className="absolute right-0 top-full mt-1 w-48 rounded-xl overflow-hidden z-10"
                                  style={{
                                    background: 'linear-gradient(145deg, rgba(30, 30, 35, 0.98) 0%, rgba(20, 20, 25, 0.98) 100%)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                  }}
                                >
                                  <button
                                    onClick={() => {
                                      handleUpdate(doc.id, { isPublic: !doc.isPublic });
                                      setMenuOpenId(null);
                                    }}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors"
                                  >
                                    {doc.isPublic ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    {doc.isPublic ? 'Make Private' : 'Make Public'}
                                  </button>
                                  <button
                                    onClick={() => handleDelete(doc.id)}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-coral hover:bg-coral/10 transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    Delete
                                  </button>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </motion.div>
      )}

      {/* Upload Modal */}
      <AnimatePresence>
        {isUploadModalOpen && (
          <UploadModal
            isOpen={isUploadModalOpen}
            onClose={() => setIsUploadModalOpen(false)}
            onUpload={handleUpload}
            isUploading={isUploading}
          />
        )}
      </AnimatePresence>

      {/* Click outside to close menu */}
      {menuOpenId && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setMenuOpenId(null)}
        />
      )}
    </div>
  );
}
